import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { CenteredDialog } from '../ui/centered-dialog';
// apps/web/src/components/users/user-table.tsx — Fase 01, Plano 16 (USR-02/USR-03); Fase 17.9 (PIN do tablet).
import { useState, type ReactElement } from 'react';
import { DeviceExpansion, type DeviceItem } from './device-expansion';
import { PermissionMatrix } from './permission-matrix';
import { rotuloStatus } from '../../lib/rotulos';
import { TablePagination } from '../ui/table-pagination';
import { useClientPagination } from '../../hooks/use-client-pagination';

export interface UserRow {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly status: 'ACTIVE' | 'DISABLED';
  readonly role: { readonly id: string; readonly name: string };
  readonly permissions: readonly string[];
  readonly temPin?: boolean;
  readonly devices?: readonly DeviceItem[];
}

export interface UserTableProps {
  readonly users: readonly UserRow[];
  readonly loading?: boolean;
  readonly onRefresh?: () => void;
  readonly onDeactivateRequested?: (user: UserRow) => void;
  readonly onEditRequested?: (user: UserRow) => void;
  readonly onResetLinkRequested?: (user: UserRow) => void;
  readonly onReactivateRequested?: (user: UserRow) => void;
  readonly onDefinirPinRequested?: (user: UserRow) => void;
}

export function UserTable({
  users,
  loading,
  onRefresh,
  onDeactivateRequested,
  onEditRequested,
  onResetLinkRequested,
  onReactivateRequested,
  onDefinirPinRequested,
}: UserTableProps): ReactElement {
  const [expandedDevicesUserId, setExpandedDevicesUserId] = useState<string | null>(null);
  const [selectedRoleForMatrix, setSelectedRoleForMatrix] = useState<{ id: string; name: string } | null>(null);
  const pagination = useClientPagination(users);

  if (loading) {
    return <div className="p-6 text-center text-sm text-slate-500">Carregando lista de usuários...</div>;
  }

  return (
    <div className="user-table space-y-4">
      {selectedRoleForMatrix && (
        <CenteredDialog title={`Permissões do perfil ${selectedRoleForMatrix.name}`} onClose={() => setSelectedRoleForMatrix(null)}><div className="p-5">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-slate-500">Edição de Perfil Ativo</span>
            <button
              type="button"
              onClick={() => setSelectedRoleForMatrix(null)}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Fechar Matriz
            </button>
          </div>
          <PermissionMatrix
            roleId={selectedRoleForMatrix.id}
            roleName={selectedRoleForMatrix.name}
            onSaveSuccess={() => {
              onRefresh?.();
            }}
          />
        </div></CenteredDialog>
      )}

      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white shadow-sm">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-700 font-medium">
              <th className="p-3">Usuário</th>
              <th className="p-3">Nome</th>
              <th className="p-3 hidden md:table-cell">E-mail</th>
              <th className="p-3">Perfil</th>
              <th className="p-3">Status</th>
              <th className="p-3">PIN Tablet</th>
              <th className="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-slate-500">
                  Nenhum usuário cadastrado.
                </td>
              </tr>
            ) : (
              pagination.pageItems.map((user) => {
                const isDevicesExpanded = expandedDevicesUserId === user.id;
                return (
                  <tr key={user.id} className="hover:bg-slate-50/50">
                    <td className="p-3 font-medium text-slate-900">{user.username}</td>
                    <td className="p-3 text-slate-700">{user.displayName}</td>
                    <td className="p-3 hidden md:table-cell text-slate-500">{user.email ?? '-'}</td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => setSelectedRoleForMatrix({ id: user.role.id, name: user.role.name })}
                        className="text-blue-600 hover:underline font-medium focus:outline-none"
                        aria-label={`Editar permissões do perfil ${user.role.name}`}
                      >
                        {user.role.name}
                      </button>
                    </td>
                    <td className="p-3">
                      {user.status === 'ACTIVE' ? (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                          {rotuloStatus(user.status)}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-600">
                          {rotuloStatus(user.status)}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {user.temPin ? (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800">
                          Definido
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                          Não definido
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right"><DropdownMenu><DropdownMenuTrigger asChild><button type="button" className="rounded-lg border px-3 py-2 text-sm" aria-label={`Ações de ${user.username}`}>Ações</button></DropdownMenuTrigger><DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => onEditRequested?.(user)}>Editar usuário</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => onDefinirPinRequested?.(user)}>
                        {user.temPin ? 'Redefinir PIN' : 'Definir PIN'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setExpandedDevicesUserId(isDevicesExpanded ? null : user.id)}>{isDevicesExpanded ? 'Ocultar dispositivos' : 'Ver dispositivos'}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {user.status === 'ACTIVE' ? <><DropdownMenuItem onSelect={() => onResetLinkRequested?.(user)}>Gerar link de senha</DropdownMenuItem><DropdownMenuItem variant="destructive" onSelect={() => onDeactivateRequested?.(user)}>Desativar usuário</DropdownMenuItem></> : <DropdownMenuItem onSelect={() => onReactivateRequested?.(user)}>Reativar usuário</DropdownMenuItem>}
                    </DropdownMenuContent></DropdownMenu></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {users.length > 0 && (
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={users.length}
            itemLabel="usuários"
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        )}
      </div>

      {expandedDevicesUserId && (
        <DeviceExpansion
          userId={expandedDevicesUserId}
          userName={users.find((u) => u.id === expandedDevicesUserId)?.username ?? ''}
          devices={users.find((u) => u.id === expandedDevicesUserId)?.devices ?? []}
          onDeviceRevoked={() => onRefresh?.()}
        />
      )}
    </div>
  );
}
